// The ragdoll lab, `?scene=ragdoll` (gate B5 and the hit reactions): one figure of each card standing on uneven ground
// around a step, defeated by a test emitter of `combat:defeated` with seeded directions and severities (the stand
// respawns once its ragdoll has dissolved), plus scripted `combat:hit` and `combat:move` emitters for the reaction
// and pose specs. The sim side: a SceneDef for the sim host (its world, its modules: the lab's stands and emitter, and
// the bodies module) and a monitor that measures every ragdoll (the soak spec's numbers and the invariants).
// Test data (positions, the emitter's ranges) lives here, as the figure lab's route does; tuning lives in content.
import type { CollisionWorld, LayerTable } from "@rpg/collision";
import { partLowest, ragdollLimitExcess, type ResolvedRagdoll } from "@rpg/figure";
import type { GameModule } from "@rpg/kernel";
import type { RngStreams } from "@rpg/runtime";
import type { CombatHit, GameEvents, Vec3 } from "../core";
import type { SceneDef } from "../sim/host";
import { BODIES_C, bodiesTables, createBodies, type Bodies, type BodiesTables } from "./module";
import type { SpikeContent } from "../content";

/** Collision layers: the ground (terrain and the step) and ragdoll parts (which meet the ground, not each other). */
export const RAGDOLL_LAYERS: LayerTable = { ground: [], ragdoll: ["ground"] };
export const RAGDOLL_SEED = 0xb0d1e5;
/** The emitter's RNG stream. */
export const DEFEAT_STREAM = "defeats";
/** Rapier's solver iterations in this world (measured over 200 seeded defeats in Node): at 24 the worst joint goes
 *  0.13 rad past its limit, at 16 0.16 to 0.20, at 8 well past 0.2 (the arms slip on landing); 24 costs about 30 %
 *  more sim time than 16. */
export const RAGDOLL_SOLVER_ITERATIONS = 24;

const FIELD = 16; // the heightfield's extent, m
const CELLS = 40;
/** The step: a block at the middle, its top `STEP.top` m up. */
export const STEP = { x: 0, z: 0, hx: 1.1, hz: 0.7, top: 0.5 };
/** The order the stands go round the ring, one card each. */
export const STAND_CARDS = ["wanderer", "brute", "shade", "villager", "wisp", "elder", "child"] as const;
const RING = 2.25;

/** The uneven ground's height at (x, z). */
export function labGround(x: number, z: number): number {
  return 0.18 * Math.sin(0.8 * x + 0.3) * Math.cos(0.7 * z) + 0.08 * Math.sin(1.7 * x - 1.1 * z);
}

/** The emitter's ranges (lab test data). */
export const EMITTER = { gap: [0.12, 0.4] as [number, number], severity: [0.2, 1] as [number, number], respawn: 0.6 };

/** A stand: a figure's place, its card and whether it stands. */
export interface Stand {
  entity: number;
  card: string;
  home: [number, number];
  x: number;
  z: number;
  y: number;
  yaw: number;
  alive: boolean;
  respawnAt: number;
  defeats: number;
  /** A knockback under way: velocity (m/s) and seconds left. */
  kb: { vx: number; vz: number; left: number } | null;
  ragdoll: string | null;
}

/** A scripted event the handle queues for the next step. */
export type Scripted =
  | { kind: "hit"; hit: CombatHit }
  | { kind: "defeat"; d: GameEvents["combat:defeated"] }
  | { kind: "move"; m: GameEvents["combat:move"] };

/** The lab's state: plain data, saved and hashed. */
export interface LabState {
  t: number;
  auto: boolean;
  nextDefeat: number;
  defeats: number;
  queue: Scripted[];
  stands: Stand[];
}

export const LAB_C = "ragdoll-lab/State";

/** What the monitor records over a run (the soak spec reads it). */
export interface RagdollReport {
  defeats: number;
  spawned: number;
  gone: number;
  byCard: Record<string, number>;
  byTier: Record<string, number>;
  /** Ragdolls whose bodies or joints differ from their list's parts and links. */
  partMismatches: string[];
  settle: { max: number; mean: number; unsettled: string[]; byCard: Record<string, number> };
  /** The deepest any part went under the ground (m), and where. */
  below: { depth: number; at: string };
  /** The furthest any joint went past its list's limits (rad), and where. */
  limit: { excess: number; at: string };
  /** Gone ragdolls that left a body or joint behind. */
  leftovers: string[];
  /** Steps measured. */
  steps: number;
}

const freshReport = (): RagdollReport => ({
  defeats: 0,
  spawned: 0,
  gone: 0,
  byCard: {},
  byTier: {},
  partMismatches: [],
  settle: { max: 0, mean: 0, unsettled: [], byCard: {} },
  below: { depth: -1, at: "" },
  limit: { excess: 0, at: "" },
  leftovers: [],
  steps: 0,
});

/** The ragdoll lab's sim: its scene def, and once set up, its state, bodies, ground and report. */
export interface RagdollLab {
  def: SceneDef;
  tables: BodiesTables;
  /** After the host has set the scene up: */
  state(): LabState;
  bodies(): Bodies;
  collision(): CollisionWorld;
  /** The ground's height under (x, z) (a ray down through the world: the terrain or the step). */
  groundAt(x: number, z: number): number;
  report(): RagdollReport;
  resetReport(): void;
  /** Queue a scripted event for the next step. */
  script(s: Scripted): void;
  /** Turn the defeat emitter on or off. */
  setAuto(on: boolean): void;
  /** A stand's chest point (where the emitter aims). */
  chest(stand: Stand): Vec3;
}

/** Build the lab's sim from the content. */
export function createRagdollLab(content: SpikeContent): RagdollLab {
  const tables = bodiesTables(content);
  let lab: LabState | null = null;
  let bodies: Bodies | null = null;
  let world: CollisionWorld | null = null;
  let report = freshReport();
  const seen = new Map<string, { card: string; bodies: string[]; joints: string[]; tier: string; entity: number }>();
  const settleTimes: number[] = [];

  const groundAt = (x: number, z: number): number => world?.castRay({ x, y: 20, z }, { x: 0, y: -1, z: 0 }, 40, { layers: ["ground"] })?.point.y ?? labGround(x, z);
  const standOf = (entity: number): Stand | undefined => lab?.stands.find((s) => s.entity === entity);
  const chestHeight = (card: string): number => {
    const rd = bodies?.resolved(card);
    return rd ? rd.parts[0]!.at.y : 0.9;
  };
  const chest = (s: Stand): Vec3 => [s.x, s.y + chestHeight(s.card), s.z];

  const monitor = (): void => {
    if (!bodies || !world) return;
    const live = bodies.state().ragdolls;
    report.steps++;
    const ids = new Set(live.map((r) => r.state.id));
    for (const r of live) {
      const rd = bodies.resolved(r.card) as ResolvedRagdoll;
      if (!seen.has(r.state.id)) {
        seen.set(r.state.id, { card: r.card, bodies: [...r.state.bodies], joints: [...r.state.joints], tier: r.state.tier, entity: r.entity });
        report.spawned++;
        report.byCard[r.card] = (report.byCard[r.card] ?? 0) + 1;
        report.byTier[r.state.tier] = (report.byTier[r.state.tier] ?? 0) + 1;
        const links = rd.parts.filter((p) => p.link).length;
        const present = r.state.bodies.filter((b) => world!.has(b)).length;
        const joints = r.state.joints.filter((j) => world!.hasJoint(j)).length;
        if (r.state.bodies.length !== rd.parts.length || present !== rd.parts.length || joints !== links || r.state.tier !== rd.list.tier)
          report.partMismatches.push(`${r.state.id} (${r.card}): ${present} bodies and ${joints} joints, list has ${rd.parts.length} parts and ${links} links`);
      }
      if (r.state.settledAt !== null && !settleTimes[Number(r.state.id.slice(2))]) {
        settleTimes[Number(r.state.id.slice(2))] = r.state.settledAt;
        report.settle.byCard[r.card] = Math.max(report.settle.byCard[r.card] ?? 0, r.state.settledAt);
      }
      if (r.state.unsettled && !report.settle.unsettled.some((u) => u.startsWith(`${r.state.id} `))) report.settle.unsettled.push(`${r.state.id} ${r.card}`);
      if (r.state.phase === "gone") continue;
      // every part's lowest point against the ground under it
      rd.parts.forEach((p, i) => {
        const b = r.state.bodies[i]!;
        if (!world!.has(b)) return;
        const low = partLowest(p, world!.pose(b));
        const d = groundAt(low.x, low.z) - low.y;
        if (d > report.below.depth) report.below = { depth: d, at: `${r.state.id} ${r.card}/${p.id} at ${r.state.age.toFixed(2)} s` };
      });
      const e = ragdollLimitExcess(world, r.state, rd);
      if (e.excess > report.limit.excess) report.limit = { excess: e.excess, at: `${r.state.id} ${r.card}/${e.part} at ${r.state.age.toFixed(2)} s` };
    }
    // the ragdolls gone since the last step: none of their bodies or joints may be left
    for (const [id, s] of seen) {
      if (ids.has(id) || (s as { done?: boolean }).done) continue;
      (s as { done?: boolean }).done = true;
      report.gone++;
      const left = [...s.bodies.filter((b) => world!.has(b)), ...s.joints.filter((j) => world!.hasJoint(j))];
      if (left.length) report.leftovers.push(`${id}: ${left.join(", ")}`);
    }
    const st = settleTimes.filter((x) => x !== undefined);
    report.settle.max = st.length ? Math.max(...st) : 0;
    report.settle.mean = st.length ? st.reduce((a, b) => a + b, 0) / st.length : 0;
  };

  const def: SceneDef = {
    name: "ragdoll",
    layers: RAGDOLL_LAYERS,
    seed: RAGDOLL_SEED,
    actions: {},
    solverIterations: RAGDOLL_SOLVER_ITERATIONS,
    setup(collision: CollisionWorld, rng: RngStreams) {
      world = collision;
      const rand = rng.stream(DEFEAT_STREAM);
      const state: LabState = { t: 0, auto: false, nextDefeat: 0, defeats: 0, queue: [], stands: [] };
      lab = state;
      bodies = createBodies({
        collision,
        tables,
        layer: "ragdoll",
        lookup: {
          card: (e) => {
            const s = standOf(e);
            return s && s.alive ? s.card : null;
          },
          root: (e) => {
            const s = standOf(e);
            return s ? { position: [s.x, s.y, s.z], yaw: s.yaw, ...(s.kb ? { velocity: [s.kb.vx, 0, s.kb.vz] as Vec3 } : {}) } : null;
          },
          knockback: (e, kb) => {
            const s = standOf(e);
            if (s) s.kb = { vx: (kb.dir[0] * kb.distance) / kb.seconds, vz: (kb.dir[2] * kb.distance) / kb.seconds, left: kb.seconds };
          },
          defeated: (e, id) => {
            const s = standOf(e);
            if (!s) return;
            s.alive = false;
            s.ragdoll = id;
            s.kb = null;
            s.defeats++;
          },
          gone: (e) => {
            const s = standOf(e);
            if (!s) return;
            s.ragdoll = null;
            s.respawnAt = state.t + EMITTER.respawn;
          },
        },
      });

      const module: GameModule<GameEvents> = {
        id: "ragdoll-lab",
        version: "0.1.0",
        setup(ctx) {
          // the clock and the emitters: scripted events first, then the seeded defeats
          ctx.registerSystem(
            ({ dt, events }) => {
              state.t += dt;
              for (const s of state.queue.splice(0)) {
                if (s.kind === "hit") events.emit("combat:hit", s.hit);
                else if (s.kind === "move") events.emit("combat:move", s.m);
                else {
                  state.defeats++;
                  events.emit("combat:defeated", s.d);
                }
              }
              if (!state.auto || state.t < state.nextDefeat) return;
              const up = state.stands.filter((s) => s.alive);
              if (!up.length) return;
              const s = up[Math.floor(rand.next() * up.length)]!;
              const yaw = rand.range(0, Math.PI * 2);
              const dir: Vec3 = [Math.sin(yaw), rand.range(-0.15, 0.1), Math.cos(yaw)];
              const m = Math.hypot(...dir);
              const c = chest(s);
              const point: Vec3 = [c[0] - (dir[0] / m) * 0.2 + rand.jitter(0.05), c[1] + rand.jitter(0.25), c[2] - (dir[2] / m) * 0.2 + rand.jitter(0.05)];
              state.defeats++;
              events.emit("combat:defeated", { entity: s.entity, by: 0, point, dir: [dir[0] / m, dir[1] / m, dir[2] / m], severity: rand.range(EMITTER.severity[0], EMITTER.severity[1]) });
              state.nextDefeat = state.t + rand.range(EMITTER.gap[0], EMITTER.gap[1]);
            },
            { name: "ragdoll-lab: emitter", order: -10 },
          );
          // the stands: a knockback slides a stand over the ground; a stand whose ragdoll has gone comes back
          ctx.registerSystem(
            ({ dt }) => {
              for (const s of state.stands) {
                if (s.kb) {
                  s.x += s.kb.vx * dt;
                  s.z += s.kb.vz * dt;
                  s.y = groundAt(s.x, s.z);
                  s.kb.left -= dt;
                  if (s.kb.left <= 0) s.kb = null;
                }
                if (!s.alive && s.ragdoll === null && state.t >= s.respawnAt) {
                  s.alive = true;
                  [s.x, s.z] = s.home;
                  s.y = groundAt(s.x, s.z);
                }
              }
            },
            { name: "ragdoll-lab: stands", order: 0 },
          );
          ctx.registerSystem(() => collision.step(), { name: "ragdoll-lab: physics step", order: 100 });
          ctx.registerSystem(() => monitor(), { name: "ragdoll-lab: monitor", order: 120 });
          ctx.registerSaveSlice({
            serialize: () => structuredClone(state),
            deserialize: (data) => Object.assign(state, structuredClone(data as LabState)),
          });
          ctx.registerAction("ragdoll-lab/script", (p) => void state.queue.push(structuredClone(p as Scripted)));
          ctx.registerAction("ragdoll-lab/auto", (p) => {
            state.auto = (p as { on: boolean }).on;
            state.nextDefeat = state.t;
          });
        },
      };

      const heights: number[] = [];
      for (let iz = 0; iz <= CELLS; iz++) for (let ix = 0; ix <= CELLS; ix++) heights.push(labGround((ix / CELLS - 0.5) * FIELD, (iz / CELLS - 0.5) * FIELD));
      return {
        parts: {
          modules: [module, bodies.module],
          content: {
            meta: { id: "ragdoll-lab", displayName: "Ragdoll lab", version: "0.1.0" },
            requiresModules: [
              { id: "ragdoll-lab", versionRange: "^0.1.0" },
              { id: "bodies", versionRange: "^0.1.0" },
            ],
            build({ world: w }) {
              collision.add({ id: "terrain", kind: "fixed", colliders: [{ shape: { type: "heightfield", rows: CELLS, cols: CELLS, heights, size: { x: FIELD, y: 1, z: FIELD } }, layer: "ground", tag: "grass", friction: 0.9 }] });
              collision.add({
                id: "step",
                kind: "fixed",
                position: { x: STEP.x, y: (STEP.top - 1) / 2, z: STEP.z },
                colliders: [{ shape: { type: "cuboid", halfExtents: { x: STEP.hx, y: (STEP.top + 1) / 2, z: STEP.hz } }, layer: "ground", tag: "stone", friction: 0.9 }],
              });
              collision.step(); // queries see the world after a step
              const lab = w.createEntity();
              w.addComponent<LabState>(lab, LAB_C, state);
              STAND_CARDS.forEach((card, k) => {
                const a = (k / STAND_CARDS.length) * Math.PI * 2 + 0.2;
                const x = Math.cos(a) * RING;
                const z = Math.sin(a) * RING * 0.85;
                const entity = w.createEntity();
                // each stand faces the step, so falls go across it, beside it and away from it
                state.stands.push({ entity, card, home: [x, z], x, z, y: groundAt(x, z), yaw: Math.atan2(-x, -z), alive: true, respawnAt: 0, defeats: 0, kb: null, ragdoll: null });
              });
            },
          },
        },
        hashed: [LAB_C, BODIES_C],
        bodies: [],
        beforeTick() {},
      };
    },
  };

  return {
    def,
    tables,
    state: () => lab!,
    bodies: () => bodies!,
    collision: () => world!,
    groundAt,
    report: () => structuredClone(report),
    resetReport: () => {
      report = freshReport();
      seen.clear();
      settleTimes.length = 0;
      // ragdolls already falling are measured from now as if new
    },
    script: (s) => void lab!.queue.push(structuredClone(s)),
    setAuto: (on) => {
      lab!.auto = on;
      lab!.nextDefeat = lab!.t;
    },
    chest,
  };
}
